import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../../../components/Modal';
import TabbedModalLayout, { TabDef } from '../../../../components/TabbedModalLayout';
import SaveDot, { SaveState } from '../../../../components/SaveDot';
import StatusPill from '../../../../components/StatusPill';
import { useConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  EditIcon,
  FileTextIcon,
  LayersIcon,
  SlidersIcon
} from '../../../../components/IconButton/icons';
import { useUserRole } from '../../../../utils/useUserRole';
import {
  Employee,
  EmployeeFormData,
  EmployeeLeave,
  ChargeRate,
  ChargeRateFormData,
  FormErrors,
  LeaveSettingsDraft,
  validateEmail,
  validateRequired,
  getEmployeeName
} from '../../config';
import DetailsTab from './DetailsTab';
import SignatureTab from './SignatureTab';
import ChargeRatesTab from './ChargeRatesTab';
import LeaveTab from './LeaveTab';
import { parseNumber } from './LeaveTab/leaveFormat';
import { useEmployeeLeave } from './useEmployeeLeave';
import './employee-detail-modal.scss';

interface EmployeeDetailModalProps {
  isOpen: boolean;
  employee: Employee | null;
  onClose: () => void;
  onSubmitBasicInfo: (employeeId: string, formData: EmployeeFormData) => Promise<boolean>;
  onAddChargeRate: (employeeId: string, formData: ChargeRateFormData) => Promise<boolean>;
  onFetchHistory: (employeeId: string) => Promise<ChargeRate[]>;
  onDeactivateEmployee: (employeeId: string) => void;
  onReactivateEmployee: (employeeId: string) => void;
  onSignatureChanged: () => void;
}

type EmployeeTab = 'details' | 'leave' | 'rates' | 'signature';

const TABS: TabDef[] = [
  { id: 'details', label: 'Details', icon: <SlidersIcon /> },
  { id: 'leave', label: 'Leave', icon: <LayersIcon /> },
  { id: 'rates', label: 'Charge rates', icon: <FileTextIcon /> },
  { id: 'signature', label: 'Signature', icon: <EditIcon /> }
];

const EMPTY_DRAFT: EmployeeFormData = {
  first_name: '',
  last_name: '',
  type: 'Engineer',
  role: 'basic',
  email: '',
  position_title: '',
  registration_number: '',
  is_cpeng: false,
  signature_scale: 1,
  signature_offset_x_mm: 0,
  signature_offset_y_mm: 0,
  standard_hours_per_day: '7.5',
  default_start_time: '08:30',
  lunch_start_time: '12:00',
  lunch_minutes: '60'
};

const EMPTY_LEAVE_DRAFT: LeaveSettingsDraft = {
  accrual_start_date: '',
  opening_annual_hours: '0',
  opening_sick_hours: '0'
};

const draftFromEmployee = (employee: Employee): EmployeeFormData => ({
  first_name: employee.first_name,
  last_name: employee.last_name,
  type: employee.type,
  role: employee.role,
  email: employee.email || '',
  position_title: employee.position_title || '',
  registration_number: employee.registration_number || '',
  is_cpeng: employee.is_cpeng || false,
  signature_scale: Number(employee.signature_scale ?? 1),
  signature_offset_x_mm: Number(employee.signature_offset_x_mm ?? 0),
  signature_offset_y_mm: Number(employee.signature_offset_y_mm ?? 0),
  standard_hours_per_day: String(employee.standard_hours_per_day ?? 7.5),
  default_start_time: (employee.default_start_time ?? '08:30').slice(0, 5),
  lunch_start_time: (employee.lunch_start_time ?? '12:00').slice(0, 5),
  lunch_minutes: String(employee.lunch_minutes ?? 60)
});

// Filled from the stored settings, not from the totals: sick leave resets
// each 1 January, so this year's sick opening is zero for anyone who started
// in an earlier year, and a form filled from it would save that zero back
// over the real figure.
const leaveDraftFromData = (leave: EmployeeLeave): LeaveSettingsDraft => ({
  accrual_start_date: leave.profile.accrual_start_date ?? '',
  opening_annual_hours: String(leave.profile.opening_annual_hours),
  opening_sick_hours: String(leave.profile.opening_sick_hours)
});

// Compared as values, not as typed: "7.50" against a stored 7.5 is not an edit.
const employeeKey = (draft: EmployeeFormData): string => JSON.stringify({
  ...draft,
  first_name: draft.first_name.trim(),
  last_name: draft.last_name.trim(),
  standard_hours_per_day: Number(draft.standard_hours_per_day),
  lunch_minutes: Number(draft.lunch_minutes)
});

const profileKey = (draft: LeaveSettingsDraft): string => JSON.stringify([
  draft.accrual_start_date,
  parseNumber(draft.opening_annual_hours) ?? 0,
  parseNumber(draft.opening_sick_hours) ?? 0
]);

const validateDetails = (draft: EmployeeFormData): FormErrors => {
  const errors: FormErrors = {};

  if (!validateRequired(draft.first_name)) errors.first_name = 'First name is required';
  if (!validateRequired(draft.last_name)) errors.last_name = 'Last name is required';
  if (!draft.type) errors.type = 'Employee type is required';
  if (!draft.role) errors.role = 'Role is required';
  if (draft.email && !validateEmail(draft.email)) errors.email = 'Please enter a valid email address';

  const dayHours = Number(draft.standard_hours_per_day);
  if (!draft.standard_hours_per_day.trim() || !Number.isFinite(dayHours) || dayHours <= 0 || dayHours > 24) {
    errors.standard_hours_per_day = 'Enter the standard hours per day (between 0.5 and 24)';
  }

  if (!/^\d{2}:\d{2}$/.test(draft.default_start_time)) {
    errors.default_start_time = 'Enter the usual start time';
  }

  const lunchMinutes = Number(draft.lunch_minutes);
  if (
    !/^\d{2}:\d{2}$/.test(draft.lunch_start_time) ||
    draft.lunch_minutes.trim() === '' ||
    !Number.isInteger(lunchMinutes) ||
    lunchMinutes < 0 ||
    lunchMinutes > 240
  ) {
    errors.lunch = 'Enter when lunch starts and its length in minutes (0 to 240)';
  }

  return errors;
};

const validateLeave = (draft: LeaveSettingsDraft): FormErrors => {
  const errors: FormErrors = {};

  if (draft.opening_annual_hours.trim() && parseNumber(draft.opening_annual_hours) === null) {
    errors.opening_annual_hours = 'Enter the hours as a number';
  }
  if (draft.opening_sick_hours.trim() && parseNumber(draft.opening_sick_hours) === null) {
    errors.opening_sick_hours = 'Enter the hours as a number';
  }

  return errors;
};

/**
 * The employee, for a manager: details, leave, charge rates and signature.
 *
 * One draft and one Save. Everything typed into a field is held here until
 * Save is pressed, across tab switches, and Save leaves the modal open so the
 * effect of a change (a new balance) can be seen. What saves at once instead
 * are the things that are rows of their own: a charge rate, a leave
 * adjustment, an entitlement, a signature image.
 */
const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({
  isOpen,
  employee,
  onClose,
  onSubmitBasicInfo,
  onAddChargeRate,
  onFetchHistory,
  onDeactivateEmployee,
  onReactivateEmployee,
  onSignatureChanged
}) => {
  const { isReadOnly } = useUserRole();
  const { confirm, dialog } = useConfirmDialog();
  const employeeId = employee?.id ?? null;
  const leave = useEmployeeLeave(employeeId, isOpen);

  const [activeTab, setActiveTab] = useState<EmployeeTab>('details');
  const [draft, setDraft] = useState<EmployeeFormData>(EMPTY_DRAFT);
  const [leaveDraft, setLeaveDraft] = useState<LeaveSettingsDraft | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<boolean>(false);

  // Which employee each draft was filled from. The employee prop is replaced
  // by a fresh object after every save and signature upload; refilling the
  // draft from it each time would throw away whatever else had been typed.
  const draftForRef = useRef<string | null>(null);
  const leaveDraftForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !employee) {
      draftForRef.current = null;
      leaveDraftForRef.current = null;
      return;
    }
    if (draftForRef.current === employee.id) return;

    draftForRef.current = employee.id;
    leaveDraftForRef.current = null;
    setActiveTab('details');
    setDraft(draftFromEmployee(employee));
    setLeaveDraft(null);
    setErrors({});
    setSaveError(null);
    setSaving(false);
    setJustSaved(false);
  }, [isOpen, employee]);

  useEffect(() => {
    if (!isOpen || !employeeId || !leave.data) return;
    if (leave.data.employee_id !== employeeId) return;
    if (leaveDraftForRef.current === employeeId) return;

    leaveDraftForRef.current = employeeId;
    setLeaveDraft(leaveDraftFromData(leave.data));
  }, [isOpen, employeeId, leave.data]);

  const savedLeaveDraft = useMemo(
    () => (leave.data ? leaveDraftFromData(leave.data) : null),
    [leave.data]
  );

  const detailsDirty = !!employee && employeeKey(draft) !== employeeKey(draftFromEmployee(employee));
  const profileDirty = !!leaveDraft && !!savedLeaveDraft && profileKey(leaveDraft) !== profileKey(savedLeaveDraft);
  const isDirty = detailsDirty || profileDirty;

  const handleDraftChange = useCallback((patch: Partial<EmployeeFormData>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setJustSaved(false);
  }, []);

  const handleLeaveDraftChange = useCallback((patch: Partial<LeaveSettingsDraft>) => {
    setLeaveDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setJustSaved(false);
  }, []);

  const { saveProfile, reload: reloadLeave } = leave;

  const handleSave = useCallback(async () => {
    if (!employee || saving) return;

    // Each half is checked only if it was edited. The details are not
    // validated when they were loaded, so a record can arrive already invalid
    // (an email nobody checked); checked regardless, that would refuse an
    // opening balance and send the manager to a field they never touched.
    const detailErrors = detailsDirty ? validateDetails(draft) : {};
    const leaveErrors = leaveDraft && profileDirty ? validateLeave(leaveDraft) : {};
    const next = { ...detailErrors, ...leaveErrors };
    if (Object.keys(next).length > 0) {
      setErrors(next);
      // Take them to the tab the first problem is on; it may not be this one.
      setActiveTab(Object.keys(detailErrors).length > 0 ? 'details' : 'leave');
      return;
    }

    setErrors({});
    setSaveError(null);
    setSaving(true);
    try {
      if (detailsDirty) {
        const saved = await onSubmitBasicInfo(employee.id, draft);
        if (!saved) {
          setSaveError('The changes could not be saved. Please try again.');
          return;
        }
        // Leave is shown in days of this person's standard day, which is one
        // of the details just saved. Without this the Leave tab goes on
        // converting with the day length it loaded when the modal opened.
        await reloadLeave();
      }

      if (leaveDraft && profileDirty) {
        const failure = await saveProfile({
          accrual_start_date: leaveDraft.accrual_start_date || null,
          opening_annual_hours: parseNumber(leaveDraft.opening_annual_hours) ?? 0,
          opening_sick_hours: parseNumber(leaveDraft.opening_sick_hours) ?? 0
        });
        if (failure) {
          setSaveError(failure);
          return;
        }
      }

      setJustSaved(true);
    } finally {
      setSaving(false);
    }
  }, [
    employee,
    saving,
    draft,
    leaveDraft,
    detailsDirty,
    profileDirty,
    onSubmitBasicInfo,
    saveProfile,
    reloadLeave
  ]);

  const handleClose = useCallback(async () => {
    if (saving) return;
    if (isDirty) {
      const discard = await confirm({
        title: 'Discard changes',
        message: 'There are changes that have not been saved. Close and lose them?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'warning'
      });
      if (!discard) return;
    }
    onClose();
  }, [saving, isDirty, confirm, onClose]);

  const handleToggleActive = useCallback(async () => {
    if (!employee) return;
    const name = getEmployeeName(employee);
    // This closes the modal, and not through handleClose, so it has to say
    // for itself what closing will cost.
    const unsaved = isDirty
      ? <p>Your unsaved changes to this employee will be lost.</p>
      : null;

    const ok = await confirm(employee.is_active
      ? {
          title: 'Deactivate employee',
          message: (
            <>
              <p><strong>{name}</strong></p>
              <p>
                They will no longer be able to sign in, and any existing session
                ends within 1 hour. Their data and history are preserved, and
                they stop earning leave from today.
              </p>
              <p>
                For immediate lockout, also ban the user in the Supabase
                dashboard (Authentication &gt; Users).
              </p>
              {unsaved}
            </>
          ),
          confirmLabel: 'Deactivate',
          tone: 'danger'
        }
      : {
          title: 'Reactivate employee',
          message: (
            <>
              <p><strong>{name}</strong></p>
              <p>
                They will be restored to active status, able to sign in and be
                assigned to projects again. They earn leave again from today.
              </p>
              {unsaved}
            </>
          ),
          confirmLabel: 'Reactivate',
          tone: 'info'
        });
    if (!ok) return;

    if (employee.is_active) {
      onDeactivateEmployee(employee.id);
    } else {
      onReactivateEmployee(employee.id);
    }
    onClose();
  }, [employee, isDirty, confirm, onDeactivateEmployee, onReactivateEmployee, onClose]);

  if (!employee) return null;

  const saveState: SaveState = saving
    ? 'saving'
    : saveError
      ? 'error'
      : isDirty
        ? 'dirty'
        : justSaved
          ? 'saved'
          : 'idle';
  const saveLabel: Record<SaveState, string> = {
    idle: '',
    dirty: 'Unsaved changes',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Not saved'
  };

  const fieldsDisabled = saving || isReadOnly;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <span className="pdm-title">
          <span className="pdm-title__text">{getEmployeeName(employee)}</span>
        </span>
      }
      size="md"
      hasTabs
      noPadding
      scrollable
      hasFooter
      // The form-ui scope sits on the modal's own root: Modal portals to
      // <body>, and the footer is a sibling of the content, so a class on a
      // div inside the children reaches neither.
      className="form-ui employee-detail-modal"
      headerStatus={
        <>
          {saveState !== 'idle' && <SaveDot state={saveState} label={saveLabel[saveState]} />}
          <StatusPill
            label={employee.is_active ? 'Active' : 'Inactive'}
            tone={employee.is_active ? 'success' : 'neutral'}
          />
        </>
      }
      footerContent={
        <>
          <button
            type="button"
            className="pill-btn pill-btn--ghost"
            onClick={handleClose}
            disabled={saving}
          >
            {isDirty ? 'Cancel' : 'Close'}
          </button>
          <div className="employee-detail-modal__footer-right">
            {saveError && (
              <span className="employee-detail-modal__save-error" role="alert">{saveError}</span>
            )}
            <button
              type="button"
              className="pill-btn pill-btn--primary"
              onClick={handleSave}
              disabled={saving || isReadOnly || !isDirty}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </>
      }
    >
      <TabbedModalLayout
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={(id) => setActiveTab(id as EmployeeTab)}
      >
        <div className="employee-detail-modal__body">
          {activeTab === 'details' && (
            <DetailsTab
              employee={employee}
              draft={draft}
              errors={errors}
              disabled={fieldsDisabled}
              onChange={handleDraftChange}
              onToggleActive={handleToggleActive}
            />
          )}

          {activeTab === 'leave' && (
            <LeaveTab
              leave={leave}
              draft={leaveDraft ?? savedLeaveDraft ?? EMPTY_LEAVE_DRAFT}
              errors={errors}
              disabled={fieldsDisabled}
              canEdit={!isReadOnly}
              onDraftChange={handleLeaveDraftChange}
            />
          )}

          {activeTab === 'rates' && (
            <ChargeRatesTab
              isOpen={isOpen}
              employeeId={employee.id}
              disabled={isReadOnly}
              onAddChargeRate={onAddChargeRate}
              onFetchHistory={onFetchHistory}
            />
          )}

          {activeTab === 'signature' && (
            <SignatureTab
              isOpen={isOpen}
              employee={employee}
              draft={draft}
              disabled={fieldsDisabled}
              onChange={handleDraftChange}
              onSignatureChanged={onSignatureChanged}
            />
          )}
        </div>
      </TabbedModalLayout>
      {dialog}
    </Modal>
  );
};

export default EmployeeDetailModal;
